import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Mail, BellRing, MessageSquare, Phone, ShieldCheck, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQ_LABEL = {
  instant: 'Instant',
  weekly: 'Weekly digest',
  monthly: 'Monthly digest',
  never: 'Don\'t email me',
};

/**
 * Logged-in user's email preference center. Mounts on /settings.
 * Lets the user pick frequency + event types + music genres so we can
 * tailor what we send them. Also exposes a master "Unsubscribe from all"
 * for users who don't want any marketing.
 */
export default function EmailPreferencesCard({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [catalogue, setCatalogue] = useState({ event_types: [], music_genres: [], frequencies: [] });
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [cRes, pRes] = await Promise.all([
          axios.get(`${API}/email-preferences/catalogue`),
          axios.get(`${API}/email-preferences/me`, { headers }),
        ]);
        if (cancelled) return;
        setCatalogue(cRes.data);
        setPrefs(pRes.data);
      } catch (e) {
        toast.error('Could not load email preferences');
      } finally { if (!cancelled) setLoading(false); }
    };
    if (token) load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const toggleItem = (key, id) => {
    setPrefs(prev => {
      const cur = new Set(prev[key] || []);
      if (cur.has(id)) cur.delete(id); else cur.add(id);
      return { ...prev, [key]: Array.from(cur) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/email-preferences/me`, {
        unsubscribed: !!prefs.unsubscribed,
        frequency: prefs.frequency,
        event_types: prefs.event_types || [],
        music_genres: prefs.music_genres || [],
      }, { headers });
      setPrefs(r.data);
      toast.success('Email preferences saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save preferences');
    } finally { setSaving(false); }
  };

  const unsubAll = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/email-preferences/me`, {
        unsubscribed: true, frequency: 'never',
      }, { headers });
      setPrefs(r.data);
      toast.success('Unsubscribed from all marketing emails');
    } catch (e) {
      toast.error('Could not unsubscribe');
    } finally { setSaving(false); }
  };

  const resubscribe = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/email-preferences/me`, {
        unsubscribed: false, frequency: 'instant',
      }, { headers });
      setPrefs(r.data);
      toast.success('Welcome back! Pick your preferences below.');
    } catch (e) {
      toast.error('Could not resubscribe');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <Card className="bg-muted/60 border-border" data-testid="email-prefs-card-loading">
        <CardContent className="p-6 flex items-center gap-3 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading email preferences…
        </CardContent>
      </Card>
    );
  }
  if (!prefs) return null;

  const isUnsub = !!prefs.unsubscribed;

  return (
    <Card className="bg-muted/60 border-border" data-testid="email-prefs-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <BellRing className="h-5 w-5 text-fuchsia-400" /> Email Preferences
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Tell us what you love and how often you want to hear from us. We only send what matches.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {isUnsub ? (
          <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-4" data-testid="email-prefs-unsubscribed">
            <p className="text-sm font-semibold">You're unsubscribed from all marketing emails.</p>
            <p className="text-xs text-muted-foreground mt-1">You'll still receive transactional emails (receipts, ticket confirmations, security notices).</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={resubscribe} disabled={saving} data-testid="email-prefs-resubscribe">
              Resubscribe and customize
            </Button>
          </div>
        ) : (
          <>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">How often?</p>
              <div className="grid grid-cols-2 gap-2">
                {(catalogue.frequencies || []).map(f => (
                  <label key={f} className={`flex items-center gap-2 rounded-lg border p-2.5 cursor-pointer transition text-sm ${prefs.frequency === f ? 'border-indigo-500 bg-indigo-500/10' : 'border-border hover:bg-muted/50'}`}>
                    <input type="radio" name="email-freq" checked={prefs.frequency === f} onChange={() => setPrefs(p => ({ ...p, frequency: f }))} data-testid={`email-prefs-freq-${f}`} />
                    {FREQ_LABEL[f] || f}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Event types you love</p>
              <div className="flex flex-wrap gap-2">
                {(catalogue.event_types || []).map(opt => {
                  const on = (prefs.event_types || []).includes(opt.id);
                  return (
                    <button key={opt.id} type="button" onClick={() => toggleItem('event_types', opt.id)} data-testid={`email-prefs-evt-${opt.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${on ? 'bg-indigo-500 text-white border-indigo-500' : 'bg-background border-border hover:bg-muted'}`}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Music vibes</p>
              <div className="flex flex-wrap gap-2">
                {(catalogue.music_genres || []).map(opt => {
                  const on = (prefs.music_genres || []).includes(opt.id);
                  return (
                    <button key={opt.id} type="button" onClick={() => toggleItem('music_genres', opt.id)} data-testid={`email-prefs-genre-${opt.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${on ? 'bg-fuchsia-500 text-white border-fuchsia-500' : 'bg-background border-border hover:bg-muted'}`}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 gap-3">
              <Button variant="ghost" size="sm" className="text-rose-500 hover:bg-rose-500/10" onClick={unsubAll} disabled={saving} data-testid="email-prefs-unsub-all">
                <Mail className="h-4 w-4 mr-2" /> Unsubscribe from all
              </Button>
              <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="email-prefs-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save preferences
              </Button>
            </div>
          </>
        )}

        {/* ===== SMS Preferences ===== */}
        <SMSPreferencesSection token={token} />
      </CardContent>
    </Card>
  );
}

/**
 * SMS preference subsection — lives inside the same card per the user's
 * request. Handles phone verification (Twilio Verify OTP, mock-friendly)
 * plus marketing opt-in + quiet hours + frequency cap.
 */
function SMSPreferencesSection({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [phoneInput, setPhoneInput] = useState('');
  const [verifyStep, setVerifyStep] = useState('idle'); // idle | sent
  const [code, setCode] = useState('');
  const [mockHint, setMockHint] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/sms-preferences/me`, { headers });
        if (!cancelled) {
          setPrefs(r.data);
          setPhoneInput(r.data.phone_e164 || '');
        }
      } catch {
        // not fatal — feature simply not configured
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const startVerify = async () => {
    if (!phoneInput.trim()) { toast.error('Enter a phone number'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/sms-preferences/verify-phone/start`, { phone: phoneInput }, { headers });
      setVerifyStep('sent');
      setMockHint(!!r.data.mock);
      toast.success(r.data.mock ? 'Mock OTP active — use code 123456' : 'Code sent — check your phone');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send code');
    } finally { setSaving(false); }
  };

  const confirmVerify = async () => {
    if (!code.trim()) { toast.error('Enter the code'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/sms-preferences/verify-phone/confirm`, { code }, { headers });
      toast.success('Phone verified! You can now opt in to SMS marketing.');
      setVerifyStep('idle');
      setCode('');
      const r = await axios.get(`${API}/sms-preferences/me`, { headers });
      setPrefs(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally { setSaving(false); }
  };

  const updatePrefs = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/sms-preferences/me`, patch, { headers });
      setPrefs(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save SMS preferences');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="border-t pt-4 mt-4 text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Loading SMS preferences…</div>;
  }
  if (!prefs) return null;

  return (
    <div className="border-t pt-5 mt-5 space-y-4" data-testid="sms-prefs-section">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="h-4 w-4 text-indigo-400" />
          <h4 className="text-sm font-semibold">Text Message (SMS) Preferences</h4>
        </div>
        <p className="text-xs text-muted-foreground">
          We'll only text you about events you'd love. Reply STOP to opt out at any time. Msg & data rates may apply.
        </p>
      </div>

      {/* Phone capture + Verify */}
      <div className="space-y-2">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1">
          <Phone className="h-3 w-3" /> Phone number
          {prefs.phone_verified && (
            <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-400 normal-case text-[10px]">
              <ShieldCheck className="h-3 w-3" /> Verified
            </span>
          )}
        </p>
        <div className="flex gap-2 flex-wrap">
          <Input
            type="tel"
            value={phoneInput}
            onChange={e => setPhoneInput(e.target.value)}
            placeholder="+1 (555) 123-4567"
            className="flex-1 min-w-[200px]"
            disabled={prefs.phone_verified && phoneInput === prefs.phone_e164}
            data-testid="sms-prefs-phone-input"
          />
          {!prefs.phone_verified || phoneInput !== prefs.phone_e164 ? (
            <Button size="sm" onClick={startVerify} disabled={saving || !phoneInput.trim()} data-testid="sms-prefs-verify-start">
              {saving && verifyStep === 'idle' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <KeyRound className="h-3 w-3 mr-1" />}
              {prefs.phone_verified ? 'Re-verify' : 'Send code'}
            </Button>
          ) : null}
        </div>
        {verifyStep === 'sent' && (
          <div className="flex gap-2 flex-wrap items-center" data-testid="sms-prefs-verify-step">
            <Input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="6-digit code"
              className="flex-1 min-w-[140px] max-w-[200px]"
              data-testid="sms-prefs-code-input"
            />
            <Button size="sm" onClick={confirmVerify} disabled={saving || !code.trim()} data-testid="sms-prefs-verify-confirm">
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Confirm
            </Button>
            {mockHint && (
              <span className="text-[10px] text-amber-400">Mock mode — use 123456</span>
            )}
          </div>
        )}
      </div>

      {/* Opt-in toggle (disabled until phone verified) */}
      <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition ${prefs.sms_marketing_opt_in ? 'border-indigo-500 bg-indigo-500/10' : 'border-border hover:bg-muted/40'} ${!prefs.phone_verified ? 'opacity-60 pointer-events-none' : ''}`} data-testid="sms-prefs-optin-row">
        <input
          type="checkbox"
          checked={!!prefs.sms_marketing_opt_in}
          onChange={(e) => updatePrefs({ sms_marketing_opt_in: e.target.checked })}
          disabled={saving || !prefs.phone_verified}
          className="mt-0.5"
          data-testid="sms-prefs-optin-toggle"
        />
        <div className="text-xs">
          <p className="font-medium">Yes, text me about events I'd love.</p>
          <p className="text-muted-foreground mt-0.5">
            {prefs.phone_verified
              ? "By opting in you consent to receive SMS marketing messages from Eventism. Frequency varies. Reply HELP for help, STOP to opt out."
              : "Verify your phone number above to enable this option."}
          </p>
        </div>
      </label>

      {/* Quiet hours + Frequency */}
      {prefs.sms_marketing_opt_in && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1">Frequency</p>
            <select
              value={prefs.sms_frequency_cap}
              onChange={(e) => updatePrefs({ sms_frequency_cap: e.target.value })}
              disabled={saving}
              className="w-full h-9 px-2 border rounded-md bg-background text-sm"
              data-testid="sms-prefs-freq"
            >
              <option value="daily">Up to daily</option>
              <option value="weekly">Up to weekly (recommended)</option>
              <option value="monthly">Up to monthly</option>
              <option value="unlimited">No cap</option>
            </select>
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1">Quiet hours</p>
            <div className="flex items-center gap-2 text-sm">
              <Input
                type="time"
                value={prefs.sms_quiet_hours?.start || '21:00'}
                onChange={(e) => updatePrefs({ sms_quiet_hours: { ...(prefs.sms_quiet_hours || {}), start: e.target.value } })}
                className="h-9"
                data-testid="sms-prefs-quiet-start"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="time"
                value={prefs.sms_quiet_hours?.end || '09:00'}
                onChange={(e) => updatePrefs({ sms_quiet_hours: { ...(prefs.sms_quiet_hours || {}), end: e.target.value } })}
                className="h-9"
                data-testid="sms-prefs-quiet-end"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
